import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";

/**
 * Footer — normal-sized. 3-column link grid + brand block on top,
 * slim legal/copyright bar on the bottom. No payment icons (per feedback).
 */

function FooterColumn({
  title,
  links,
}: {
  title: string;
  links: { label: string; href: string }[];
}) {
  return (
    <div>
      <h2 className="text-[12px] font-bold uppercase tracking-[0.18em] text-background/75 mb-4">
        {title}
      </h2>
      <ul className="space-y-2.5">
        {links.map((link) => (
          <li key={link.label}>
            <Link
              href={link.href}
              className="text-[14px] font-medium text-background/80 hover:text-background transition-colors"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  const t = useTranslations("footer");

  const shopLinks = [
    { label: t("gezicht"), href: "/collecties/gezicht" },
    { label: t("ogen"), href: "/collecties/ogen" },
    { label: t("lippen"), href: "/collecties/lippen" },
    { label: t("accessoires"), href: "/collecties/accessoires" },
  ];

  const infoLinks = [
    { label: t("overOns"), href: "/over-ons" },
    { label: t("ingredienten"), href: "/informatie" },
    { label: t("contact"), href: "/contact" },
    { label: t("faq"), href: "/faq" },
  ];

  const serviceLinks = [
    { label: t("verzending"), href: "/verzending" },
    { label: t("garantie"), href: "/garantie" },
    { label: t("voorwaarden"), href: "/voorwaarden" },
    { label: t("privacy"), href: "/privacy" },
    // Withdrawing consent has to be as easy as giving it (AVG Art 7(3)), and
    // the banner never comes back once answered, so the way back sits in the
    // footer of every page instead.
    { label: t("cookievoorkeuren"), href: "/privacy#cookievoorkeuren" },
  ];

  return (
    <footer className="bg-foreground text-background pb-14 lg:pb-0">
      <div className="content-wrapper py-14 lg:py-16">
        {/* ── Top grid: brand block + 3 link columns ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10 lg:gap-12 pb-10 border-b border-background/10">
          {/* Brand block */}
          <div>
            <Link href="/" className="inline-block mb-5">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/logo.svg"
                alt="Unity Cosmetics"
                width={84}
                height={28}
                className="h-7 w-auto invert"
              />
            </Link>
            <p className="text-[14px] font-medium text-background/75 leading-relaxed max-w-xs mb-5">
              {t("description")}
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.instagram.com/unitycosmetics/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-background/40 hover:text-background transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" />
                  <circle cx="12" cy="12" r="5" />
                  <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                </svg>
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=100080454486577"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="text-background/40 hover:text-background transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
                </svg>
              </a>
              <a
                href="https://www.youtube.com/user/UnityCosmetics"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="text-background/40 hover:text-background transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="4" width="20" height="16" rx="4" />
                  <path d="M10 9l5 3-5 3z" fill="currentColor" stroke="none" />
                </svg>
              </a>
            </div>
          </div>

          {/* Link columns */}
          <FooterColumn title={t("shop")} links={shopLinks} />
          <FooterColumn title={t("info")} links={infoLinks} />
          <FooterColumn title={t("service")} links={serviceLinks} />
        </div>

        {/* ── Bottom bar: copyright + credit ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-6 text-[12px] font-medium text-background/65">
          <p>
            &copy; {new Date().getFullYear()} DS Cosmetics en Skincare B.V.
            &middot; KvK 99636018 &middot; {t("vatLabel")} NL869070514B01
          </p>
          <p>
            {t("madeBy")}{" "}
            <Link
              href="/colofon"
              className="text-[#d4af6f] font-semibold underline decoration-[#d4af6f]/40 underline-offset-2 hover:decoration-[#d4af6f] hover:brightness-125 transition-all"
            >
              Nurani
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
}
